// Cüz Al akışının ortak tipleri, API çağrıları ve hesapları
// (ana sayfadaki panel ve Tebrikler sayfası birlikte kullanır)
import { API_BASE_URL } from '../config';

export type ReadingMode = 'fixed' | 'rotational';
export type ReadingType = 'daily' | 'weekly' | 'yearly' | 'special';
export type CuzDurum = 'bos' | 'alindi' | 'tamamlandi';

export interface CardSettings {
  image: string | null;
  badge: string | null;
  badgeColor: string;
  shortDescription: string | null;
  fullDescription: string | null;
  isFeatured: boolean;
  priority: number;
  backgroundColor: string | null;
  showMemberCount: boolean;
  showLastMember: boolean;
}

export interface HatimGroup {
  _id: string;
  hatimAdi: string;
  description: string;
  baslangicTarihi: string;
  bitisTarihi: string;
  readingMode: ReadingMode;
  readingType: ReadingType;
  stats: {
    totalCuz: number;
    assignedCuz: number;
    availableCuz: number;
    completionPercentage: number;
  };
  hatimInfo?: {
    activeHatimNo: number;
    totalHatimCount: number;
    totalCompletedHatim: number;
    hasMultipleHatim: boolean;
  };
  memberCount: number;
  lastMember: { name: string; date: string } | null;
  cardSettings: CardSettings | null;
}

export interface CuzInfo {
  cuzNo: number;
  durum: CuzDurum;
  okuyucuAdi?: string;
  okumaTuru?: 'donerli' | 'sabit';
}

export interface HatimSekmesi {
  hatimNo: number;
  isCompleted: boolean;
  stats: { total: number; assigned: number; completed: number; available: number };
}

export interface MultiHatimInfo {
  currentHatimNo: number;
  activeHatimNo: number;
  totalHatimCount: number;
  totalCompletedHatim: number;
  hatimler: HatimSekmesi[];
  hasMultipleHatim: boolean;
}

export const MAX_CUZ_SECIMI = 10;

const decode = (value: string) => {
  try { return decodeURIComponent(value); } catch (e) { return value; }
};

// Web'de yayında olan hatim grupları
export const fetchWebGroups = async (): Promise<HatimGroup[]> => {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/groups/web-listesi`);
  } catch (e) {
    throw new Error('Sunucu ile bağlantı kurulamadı. Lütfen internet bağlantınızı kontrol edin.');
  }
  if (!response.ok) {
    throw new Error(response.status === 401
      ? 'Sunucuya erişim izni bulunamadı. Lütfen daha sonra tekrar deneyin.'
      : `Sunucu hatası (${response.status}). Lütfen tekrar deneyin.`);
  }
  const data = await response.json();
  if (!data.success) throw new Error(data.message || 'Hatim listesi alınamadı');
  return data.data.map((group: any): HatimGroup => ({
    _id: group._id,
    hatimAdi: decode(group.name || group.hatimAdi || ''),
    description: decode(group.description || ''),
    baslangicTarihi: group.startDate || group.createdAt,
    bitisTarihi: group.endDate || group.startDate || group.createdAt,
    readingMode: group.readingMode || 'fixed',
    readingType: group.readingType || 'weekly',
    stats: group.stats,
    hatimInfo: group.hatimInfo,
    memberCount: group.memberCount || 0,
    lastMember: group.lastMember || null,
    cardSettings: group.cardSettings || null
  }));
};

// Bir grubun (istenirse belirli bir hatiminin) cüz durumları
export const fetchCuzDurumu = async (
  groupId: string,
  hatimNo?: number
): Promise<{ cuzler: CuzInfo[]; hatimInfo: MultiHatimInfo | null }> => {
  const url = hatimNo
    ? `${API_BASE_URL}/api/groups/${groupId}/cuz-durumu?hatimNo=${hatimNo}`
    : `${API_BASE_URL}/api/groups/${groupId}/cuz-durumu`;
  const response = await fetch(url);
  const data = await response.json();
  if (!data.success) {
    return {
      cuzler: Array.from({ length: 30 }, (_, i) => ({ cuzNo: i + 1, durum: 'bos' as const })),
      hatimInfo: null
    };
  }
  return { cuzler: data.data, hatimInfo: data.hatimInfo || null };
};

export interface WebCuzAlCevabi {
  success: boolean;
  message?: string;
  newHatimOpened?: boolean;
  newHatimNo?: number;
  data?: { hatimNo?: number; newHatimOpened?: boolean };
}

// Tek cüz alma (birden fazla cüz sırayla, cüz başına bir istek)
export const webCuzAl = async (
  groupId: string,
  body: { ad: string; soyad: string; cuzNo: number; okumaTuru: 'donerli' | 'sabit'; hatimNo: number }
): Promise<WebCuzAlCevabi> => {
  const response = await fetch(`${API_BASE_URL}/api/groups/${groupId}/web-cuz-al`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  try {
    return await response.json();
  } catch {
    return { success: false, message: `Sunucu hatası (${response.status})` };
  }
};

// Etiketler
export const modeLabel = (mode: ReadingMode) => (mode === 'rotational' ? 'Dönerli' : 'Sabit');

export const typeLabel = (type: ReadingType) =>
  type === 'daily' ? 'Günlük' : type === 'weekly' ? 'Haftalık' : type === 'yearly' ? 'Yıllık' : 'Özel';

export const formatTarih = (iso: string) =>
  new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

// Cüz sayfa aralıkları (Sözler düzeni: her cüz 20 sayfa, 30. cüz 581–604)
export const cuzSayfalari = (cuzNo: number) => ({
  baslangic: (cuzNo - 1) * 20 + 1,
  bitis: cuzNo === 30 ? 604 : cuzNo * 20
});

// Okuma çizelgesi (eski Tebrikler sayfasındaki hesap aynen):
// günlük gruplarda her satır bir gün, diğerlerinde bir hafta;
// dönerli okumada her satırda bir sonraki cüze geçilir, sabitte aynı cüz okunur.
export interface CizelgeSatiri {
  sira: number;
  etiket: string;
  tarih: Date;
  cuz: number;
}

const GUN_MS = 1000 * 60 * 60 * 24;

export const cizelgeSatirSayisi = (gunluk: boolean, baslangic?: string, bitis?: string) => {
  if (!baslangic || !bitis) return 30;
  const diffDays = Math.ceil((new Date(bitis).getTime() - new Date(baslangic).getTime()) / GUN_MS);
  // İlk gün/hafta dahil
  const count = gunluk ? diffDays + 1 : Math.floor(diffDays / 7) + 1;
  return count > 0 ? count : 30;
};

export const okumaCizelgesi = (
  cuzNo: number,
  opts: { mode: ReadingMode; gunluk: boolean; baslangic?: string; satirSayisi: number }
): CizelgeSatiri[] => {
  const startDate = opts.baslangic ? new Date(opts.baslangic) : new Date();
  return Array.from({ length: opts.satirSayisi }, (_, i) => {
    const tarih = new Date(startDate);
    tarih.setDate(startDate.getDate() + (opts.gunluk ? i : i * 7));
    return {
      sira: i + 1,
      etiket: `${i + 1}. ${opts.gunluk ? 'Gün' : 'Hafta'}`,
      tarih,
      cuz: opts.mode === 'rotational' ? ((cuzNo - 1 + i) % 30) + 1 : cuzNo
    };
  });
};

export const cizelgeAciklamasi = (mode: ReadingMode, gunluk: boolean) =>
  mode === 'rotational'
    ? gunluk
      ? 'Dönerli Günlük Hatim - Her gün sıralı olarak bir sonraki cüze geçersiniz.'
      : 'Dönerli Haftalık Hatim - Her hafta sıralı olarak bir sonraki cüze geçersiniz.'
    : gunluk
      ? 'Sabit Günlük Hatim - Her gün aynı cüzleri okursunuz.'
      : 'Sabit Haftalık Hatim - Her hafta aynı cüzleri okursunuz.';

export const AY_KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
export const AY_UZUN = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
export const GUN_KISA = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

// "7 Şub Paz"
export const kisaTarih = (d: Date) => `${d.getDate()} ${AY_KISA[d.getMonth()]} ${GUN_KISA[d.getDay()]}`;

// "7 Şubat – 7 Mart 2027" (aynı yılsa yıl bir kez yazılır)
export const tarihAraligi = (baslangic?: string, bitis?: string) => {
  if (!baslangic) return '';
  const a = new Date(baslangic);
  const b = bitis ? new Date(bitis) : a;
  const sol = `${a.getDate()} ${AY_UZUN[a.getMonth()]}${a.getFullYear() !== b.getFullYear() ? ` ${a.getFullYear()}` : ''}`;
  return `${sol} – ${b.getDate()} ${AY_UZUN[b.getMonth()]} ${b.getFullYear()}`;
};

// "4. ve 13." / "4., 13. ve 20."
export const cuzSiralama = (cuzler: number[]) => {
  const parcalar = cuzler.map((c) => `${c}.`);
  return parcalar.length < 2 ? parcalar.join('') : `${parcalar.slice(0, -1).join(', ')} ve ${parcalar[parcalar.length - 1]}`;
};
